import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth0Provider as Auth0ProviderSDK, useAuth0 as useAuth0SDK } from '@auth0/auth0-react';
import axios from 'axios';

const Auth0Context = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Auth0ConfiguredWrapper = ({ children }) => {
  const auth0 = useAuth0SDK();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  
  // Auto-sync Auth0 user to backend after authentication
  useEffect(() => {
    const syncAuth0User = async () => {
      if (auth0.isAuthenticated && !auth0.isLoading && !syncing) {
        // Check if we already have a local token
        const existingToken = localStorage.getItem('token');
        if (existingToken) {
          // Already synced
          return;
        }
        
        setSyncing(true);
        try {
          // Get Auth0 access token
          const auth0Token = await auth0.getAccessTokenSilently();
          
          // Send to backend to sync user and get API token
          const response = await axios.post(`${API_URL}/auth0/callback`, {
            access_token: auth0Token
          });

          if (response.data.success) {
            const { access_token } = response.data;
            
            // Store API token
            localStorage.setItem('token', access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            
            // Force page reload to update auth state
            window.location.href = '/dashboard';
          }
        } catch (error) {
          console.error('Auth0 sync error:', error);
          setSyncError(error.response?.data?.detail || 'Erreur de synchronisation Auth0');
          // Clear Auth0 session on error
          auth0.logout({ logoutParams: { returnTo: window.location.origin } });
        } finally {
          setSyncing(false);
        }
      }
    };

    syncAuth0User();
  }, [auth0.isAuthenticated, auth0.isLoading]);
  
  return (
    <Auth0Context.Provider value={{ 
      isAuth0Configured: true, 
      syncing,
      syncError,
      ...auth0 
    }}>
      {children}
    </Auth0Context.Provider>
  );
};

export const Auth0Provider = ({ children }) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  const redirectUri = window.location.origin;

  // Check if Auth0 credentials are configured
  const isAuth0Configured = domain && 
                            clientId && 
                            !domain.includes('YOUR_') && 
                            !clientId.includes('YOUR_');

  if (!isAuth0Configured) {
    console.warn('Auth0 credentials not configured. Auth0 login will be disabled.');
    // Return wrapper with disabled context
    return (
      <Auth0Context.Provider value={{ isAuth0Configured: false }}>
        {children}
      </Auth0Context.Provider>
    );
  }

  return (
    <Auth0ProviderSDK
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: 'openid profile email'
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Auth0ConfiguredWrapper>{children}</Auth0ConfiguredWrapper>
    </Auth0ProviderSDK>
  );
};

export const useAuth0Integration = () => {
  const context = useContext(Auth0Context);
  
  if (!context) {
    // Fallback if context is not available
    return {
      isAuth0Configured: false,
      isAuthenticated: false,
      isLoading: false,
      user: null,
      loginWithAuth0: () => console.warn('Auth0 not configured'),
      logout: () => {},
      getAccessTokenSilently: async () => null
    };
  }

  if (!context.isAuth0Configured) {
    return {
      isAuth0Configured: false,
      isAuthenticated: false,
      isLoading: false,
      user: null,
      loginWithAuth0: () => console.warn('Auth0 not configured'),
      logout: () => {},
      getAccessTokenSilently: async () => null
    };
  }

  return {
    ...context,
    loginWithAuth0: context.loginWithRedirect
  };
};

export default Auth0Provider;
